import { Trash2, Settings, Copy, ChevronUp, ChevronDown } from 'lucide-react';
import { FormField } from '../../types/formBuilder';
import { FIELD_TYPES } from '../../types/formBuilder';
import { createNewField } from '../../utils/formBuilderHelpers';

interface FormFieldComponentProps {
  field: FormField;
  sectionId: string;
  fieldIndex: number;
  totalFields: number;
  canRemove: boolean;
  onUpdate: (sectionId: string, fieldId: string, updates: Partial<FormField>) => void;
  onRemove: (sectionId: string, fieldId: string) => void;
  onOpenSettings: (sectionId: string, fieldId: string) => void;
  onMoveUp: (sectionId: string, fieldIndex: number) => void;
  onMoveDown: (sectionId: string, fieldIndex: number) => void;
  isDragging?: boolean;
}

export const FormFieldComponent = ({
  field,
  sectionId,
  fieldIndex,
  totalFields,
  canRemove,
  onUpdate,
  onRemove,
  onOpenSettings,
  onMoveUp,
  onMoveDown,
  isDragging = false
}: FormFieldComponentProps) => {
  const fieldType = FIELD_TYPES.find(type => type.value === field.type);
  
  const handleLabelChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onUpdate(sectionId, field.id, { label: e.target.value });
  };

  const handleTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onUpdate(sectionId, field.id, { type: e.target.value as any });
  };

  const handleRequiredChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onUpdate(sectionId, field.id, { required: e.target.checked });
  };

  const handleDuplicate = () => {
    // Create a new field based on the current field
    const duplicatedField = {
      ...createNewField(),
      type: field.type,
      label: `${field.label} (Copy)`,
      variable_name: `${field.variable_name}_copy`,
      required: field.required,
      placeholder: field.placeholder,
      description: field.description,
      advanced: field.advanced,
      defaultValue: field.defaultValue,
      hidden: field.hidden,
      min: field.min,
      max: field.max,
      step: field.step,
      rows: field.rows,
      options: field.options ? [...field.options] : [],
      multiple: field.multiple,
      accept: field.accept,
      imageOptions: field.imageOptions ? [...field.imageOptions] : [],
      allowCustomValue: field.allowCustomValue,
      customValueLabel: field.customValueLabel,
      customButtonLabel: field.customButtonLabel,
      customInputPlaceholder: field.customInputPlaceholder
    };

    // Add the duplicated field to the section
    onUpdate(sectionId, duplicatedField.id, duplicatedField);
  };

  const handleDelete = () => {
    if (canRemove) {
      onRemove(sectionId, field.id);
    }
  };

  const handleOpenSettings = () => {
    onOpenSettings(sectionId, field.id);
  };

  return (
    <div className={`bg-elevated border border-subtle rounded-lg p-4 transition-all ${
      isDragging ? 'opacity-50 scale-95' : 'hover:border-brand/50'
    } ${field.hidden ? 'border-status-warning/50 bg-status-warning/5' : ''}`}>
      {/* Field Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center space-x-2">
          <div className="flex flex-col space-y-1">
            <button
              onClick={() => onMoveUp(sectionId, fieldIndex)}
              disabled={fieldIndex === 0}
              className="p-0.5 text-th-secondary hover:text-brand transition-colors rounded disabled:opacity-30 disabled:cursor-not-allowed"
              title="Move up"
            >
              <ChevronUp className="w-3 h-3" />
            </button>
            <button
              onClick={() => onMoveDown(sectionId, fieldIndex)}
              disabled={fieldIndex === totalFields - 1}
              className="p-0.5 text-th-secondary hover:text-brand transition-colors rounded disabled:opacity-30 disabled:cursor-not-allowed"
              title="Move down"
            >
              <ChevronDown className="w-3 h-3" />
            </button>
          </div>
          <div className="flex items-center space-x-2">
            {fieldType?.icon && (
              <fieldType.icon className="w-4 h-4 text-brand" />
            )}
            <span className="text-xs font-medium text-brand bg-brand/10 px-2 py-1 rounded">
              {fieldType?.label || field.type}
            </span>
          </div>
          {field.required && (
            <span className="text-xs text-status-error font-medium">*</span>
          )}
          {field.hidden && (
            <span className="text-xs text-status-warning font-medium bg-status-warning/20 px-1.5 py-0.5 rounded">
              Hidden
            </span>
          )}
          {field.advanced && (
            <span className="text-xs text-accent font-medium bg-accent/20 px-1.5 py-0.5 rounded">
              Advanced
            </span>
          )}
        </div>
        
        <div className="flex items-center space-x-1">
          <button
            onClick={handleOpenSettings}
            className="p-1.5 text-th-secondary hover:text-brand transition-colors rounded hover:bg-brand/10"
            title="Field Settings"
          >
            <Settings className="w-4 h-4" />
          </button>
          <button
            onClick={handleDuplicate}
            className="p-1.5 text-th-secondary hover:text-accent transition-colors rounded hover:bg-accent/10"
            title="Duplicate Field"
          >
            <Copy className="w-4 h-4" />
          </button>
          {canRemove && (
            <button
              onClick={handleDelete}
              className="p-1.5 text-th-secondary hover:text-status-error transition-colors rounded hover:bg-status-error/10"
              title="Delete Field"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Field Configuration */}
      <div className="space-y-3">
        {/* Label Input */}
        <div className="space-y-1">
          <label className="block text-xs font-medium text-th-secondary">
            Label
          </label>
          <input
            type="text"
            value={field.label}
            onChange={handleLabelChange}
            placeholder="Enter field label"
            className="w-full bg-surface border border-subtle rounded px-3 py-2 text-th-primary placeholder-th-tertiary focus:border-brand focus:outline-none text-sm"
          />
        </div>

        {/* Type and Settings Row */}
        <div className="grid grid-cols-2 gap-3">
          {/* Field Type */}
          <div className="space-y-1">
            <label className="block text-xs font-medium text-th-secondary">
              Type
            </label>
            <select
              value={field.type}
              onChange={handleTypeChange}
              className="w-full bg-surface border border-subtle rounded px-3 py-2 text-th-primary focus:border-brand focus:outline-none text-sm"
            >
              <option value="">Select Type</option>
              {FIELD_TYPES.map(type => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>

          {/* Variable Name */}
          <div className="space-y-1">
            <label className="block text-xs font-medium text-th-secondary">
              Variable Name
            </label>
            <input
              type="text"
              value={field.variable_name}
              onChange={(e) => onUpdate(sectionId, field.id, { variable_name: e.target.value })}
              placeholder="input_concept"
              className="w-full bg-surface border border-subtle rounded px-3 py-2 text-th-primary placeholder-th-tertiary focus:border-brand focus:outline-none text-sm font-mono"
            />
          </div>
        </div>

        {/* Required Checkbox */}
        <div className="flex items-center space-x-2">
          <label className="flex items-center space-x-2 cursor-pointer">
            <input
              type="checkbox"
              checked={field.required}
              onChange={handleRequiredChange}
              className="w-4 h-4 text-brand bg-surface border-subtle rounded focus:ring-brand focus:ring-2"
            />
            <span className="text-sm text-th-primary">Required field</span>
          </label>
        </div>

        {/* Description if provided */}
        {field.description && (
          <div className="text-xs text-th-secondary bg-surface p-2 rounded border border-subtle">
            {field.description}
          </div>
        )}
      </div>
    </div>
  );
};